import * as React from 'react';
import { motion } from 'framer-motion';

interface Props {
  /** Fraction 0..1. Animated on mount with a spring sweep. */
  value: number;
  /** Color of the filled portion (Tailwind color or CSS color). Defaults to saffron. */
  fillClass?: string;
  /** Override container height. */
  className?: string;
  /** Aria label for the bar. */
  label?: string;
}

/**
 * A Crextio-style progress bar — solid saffron fill on the left, diagonal-hatched
 * neutral fill on the empty portion. Animates from 0 to `value` on mount.
 */
export function HatchedBar({ value, fillClass = 'bg-saffron', className, label }: Props) {
  const pct = Math.max(0, Math.min(1, value));
  return (
    <div
      role={label ? 'progressbar' : undefined}
      aria-label={label}
      aria-valuenow={Math.round(pct * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      className={[
        'relative h-2 w-full overflow-hidden rounded-full',
        'bg-[image:repeating-linear-gradient(135deg,oklch(var(--muted))_0_4px,transparent_4px_9px)]',
        'ring-1 ring-border/40',
        className ?? '',
      ].join(' ')}
    >
      <motion.div
        className={['absolute inset-y-0 left-0 rounded-full', fillClass].join(' ')}
        initial={{ width: 0 }}
        animate={{ width: `${pct * 100}%` }}
        transition={{ type: 'spring', stiffness: 80, damping: 18, mass: 0.9 }}
      />
    </div>
  );
}
